/**
 * Histograme interactive cu brush selection pe Canvas.
 * 
 * Fiecare histogramă afișează distribuția unei variabile,
 * cu barele colorate proporțional cu populația.
 * Brush (drag) selectează un interval → crossfilter.
 */
import { setSelection, clearSelection, onCrossfilterChange, onCrossfilterStart, hasActiveFilters } from './crossfilter.js';
import { LABELS, UNITS, valueToColor } from './colormaps.js';

// ── Config ───────────────────────────────────────────────────────
const HIST_HEIGHT = 80;
const HIST_PADDING = { top: 4, right: 8, bottom: 18, left: 8 };
const BAR_GAP = 1;
const BRUSH_COLOR = 'rgba(99, 179, 237, 0.3)';
const BRUSH_BORDER = 'rgba(99, 179, 237, 0.8)';

// ── State ────────────────────────────────────────────────────────
const histograms = new Map();  // variable → { canvas, ctx, data, brush, ... }
let containerEl = null;

// ── Init ─────────────────────────────────────────────────────────

/**
 * Inițializează panoul de histograme.
 * @param {string} containerId - ID-ul containerului DOM
 * @param {string[]} variables - Lista de variabile de afișat
 */
export function initHistograms(containerId, variables) {
  containerEl = document.getElementById(containerId);
  if (!containerEl) return;

  containerEl.innerHTML = '';

  for (const v of variables) {
    const wrapper = document.createElement('div');
    wrapper.className = 'histogram-wrapper';
    wrapper.dataset.variable = v;

    // Label
    const label = document.createElement('div');
    label.className = 'histogram-label';
    label.innerHTML = `<span class="histogram-var-name">${LABELS[v] || v}</span>
      <span class="histogram-var-unit">${UNITS[v] || ''}</span>
      <span class="histogram-pop-badge" id="hist-pop-${v}"></span>`;
    wrapper.appendChild(label);

    // Canvas
    const canvas = document.createElement('canvas');
    canvas.className = 'histogram-canvas';
    canvas.height = HIST_HEIGHT;
    wrapper.appendChild(canvas);

    containerEl.appendChild(wrapper);

    const ctx = canvas.getContext('2d');
    const state = {
      canvas,
      ctx,
      variable: v,
      data: null,
      brush: null,       // { startX, endX } în pixel coords
      brushRange: null,   // { min, max } în data coords
      dragging: false,
      dragStartX: null,
    };

    histograms.set(v, state);

    // Event listeners pentru brush
    setupBrushEvents(state);
  }

  // Ascultat start & crossfilter-changed pentru re-draw și loading state
  onCrossfilterStart(() => {
    if (containerEl) containerEl.classList.add('loading');
  });

  onCrossfilterChange(result => {
    if (containerEl) containerEl.classList.remove('loading');
    onCrossfilterUpdate(result);
  });

  // Resize observer
  if (typeof ResizeObserver !== 'undefined') {
    const ro = new ResizeObserver(() => {
      for (const state of histograms.values()) {
        resizeCanvas(state);
        drawHistogram(state);
      }
    });
    ro.observe(containerEl);
  }
}

// ── Events ───────────────────────────────────────────────────────

function setupBrushEvents(state) {
  const { canvas } = state;

  canvas.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    canvas.setPointerCapture(e.pointerId);
    const x = e.offsetX;
    state.dragging = true;
    state.dragStartX = x;
    state.brush = { startX: x, endX: x };
  });

  canvas.addEventListener('pointermove', (e) => {
    if (!state.dragging) return;
    const x = Math.max(HIST_PADDING.left, Math.min(e.offsetX, canvas.width - HIST_PADDING.right));
    state.brush.endX = x;
    drawHistogram(state);
  });

  canvas.addEventListener('pointerup', (e) => {
    if (!state.dragging) return;
    state.dragging = false;
    canvas.releasePointerCapture(e.pointerId);

    const { brush, data } = state;
    if (!brush || !data) return;

    const minX = Math.min(brush.startX, brush.endX);
    const maxX = Math.max(brush.startX, brush.endX);
    const brushWidth = maxX - minX;

    if (brushWidth < 5) {
      // Click simplu → anulează selecția
      state.brush = null;
      state.brushRange = null;
      clearSelection(`histogram-${state.variable}`);
    } else {
      // Convertește pixeli → valori date
      const plotW = canvas.width - HIST_PADDING.left - HIST_PADDING.right;
      const dataMin = data.min;
      const dataMax = data.max;
      const dataRange = dataMax - dataMin;

      const valMin = dataMin + ((minX - HIST_PADDING.left) / plotW) * dataRange;
      const valMax = dataMin + ((maxX - HIST_PADDING.left) / plotW) * dataRange;

      state.brushRange = { min: valMin, max: valMax };

      setSelection(`histogram-${state.variable}`, {
        type: 'range',
        variable: state.variable,
        min: valMin,
        max: valMax,
      });
    }

    drawHistogram(state);
  });

  // Double-click anulează
  canvas.addEventListener('dblclick', () => {
    state.brush = null;
    state.brushRange = null;
    clearSelection(`histogram-${state.variable}`);
    drawHistogram(state);
  });
}

// ── Render ────────────────────────────────────────────────────────

function resizeCanvas(state) {
  const { canvas } = state;
  const rect = canvas.parentElement.getBoundingClientRect();
  canvas.width = rect.width;
}

function drawHistogram(state) {
  const { canvas, ctx, data, variable, brush, brushRange } = state;
  if (!data || !data.bins || data.bins.length === 0) return;

  const w = canvas.width;
  const h = canvas.height;
  const plotX = HIST_PADDING.left;
  const plotY = HIST_PADDING.top;
  const plotW = w - HIST_PADDING.left - HIST_PADDING.right;
  const plotH = h - HIST_PADDING.top - HIST_PADDING.bottom;
  const nbins = data.bins.length;
  const barW = (plotW / nbins) - BAR_GAP;

  // Clear
  ctx.clearRect(0, 0, w, h);

  // Background subtle
  ctx.fillStyle = 'rgba(255,255,255,0.03)';
  ctx.beginPath();
  ctx.roundRect(0, 0, w, h, 6);
  ctx.fill();

  // Calculează max pentru scalare
  const maxPop = Math.max(...data.bins.map(b => b.pop), 1);

  // Desenează barele
  for (let i = 0; i < nbins; i++) {
    const bin = data.bins[i];
    const x = plotX + i * (barW + BAR_GAP);
    const barH = (bin.pop / maxPop) * plotH;
    const y = plotY + plotH - barH;

    // Culoare bazată pe valorile variabilei
    const midVal = (bin.binStart + bin.binEnd) / 2;
    const colorArr = valueToColor(midVal, variable);
    let color = `rgba(${colorArr[0]}, ${colorArr[1]}, ${colorArr[2]}, ${colorArr[3] / 255})`;
    
    // Dacă e brush activ și bara e în afara selecției → muted
    if (brushRange) {
      const inBrush = bin.binEnd > brushRange.min && bin.binStart < brushRange.max;
      if (!inBrush) {
        color = 'rgba(128, 128, 128, 0.2)';
      }
    }

    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.roundRect(x, y, Math.max(barW, 1), barH, [2, 2, 0, 0]);
    ctx.fill();
  }

  // Axă X minimală (min / max)
  ctx.fillStyle = 'rgba(255,255,255,0.4)';
  ctx.font = '9px system-ui, sans-serif';
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  ctx.fillText(formatValue(data.min, variable), plotX, h - 14);
  ctx.textAlign = 'right';
  ctx.fillText(formatValue(data.max, variable), w - HIST_PADDING.right, h - 14);

  // Brush overlay
  if (brush) {
    const bx = Math.min(brush.startX, brush.endX);
    const bw = Math.abs(brush.endX - brush.startX);

    ctx.fillStyle = BRUSH_COLOR;
    ctx.fillRect(bx, plotY, bw, plotH);

    ctx.strokeStyle = BRUSH_BORDER;
    ctx.lineWidth = 1.5;
    ctx.strokeRect(bx, plotY, bw, plotH);
  }
}

function formatValue(val, variable) {
  if (val == null) return '';
  if (Math.abs(val) >= 1000) return val.toFixed(0);
  if (Math.abs(val) >= 10) return val.toFixed(1);
  return val.toFixed(2);
}

// ── Crossfilter update ──────────────────────────────────────────

function onCrossfilterUpdate(result) {
  if (!result || !result.histograms) return;

  for (const [v, state] of histograms) {
    if (result.histograms[v]) {
      state.data = result.histograms[v];
      resizeCanvas(state);
      drawHistogram(state);
    }

    // Actualizare badge populație
    const badge = document.getElementById(`hist-pop-${v}`);
    if (badge && result.hasFilter && result.stats) {
      const pop = Number(result.stats.total_pop || 0);
      badge.textContent = pop > 0 ? `${formatPopulation(pop)} pers.` : '';
      badge.style.display = pop > 0 ? '' : 'none';
    } else if (badge) {
      badge.style.display = 'none';
    }
  }
}

function formatPopulation(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toLocaleString();
}

/**
 * Actualizează histogramele cu date noi (ex: la schimbarea timestep).
 * Apelat extern de main.js.
 */
export function updateHistogramData(histogramData) {
  for (const [v, state] of histograms) {
    if (histogramData[v]) {
      state.data = histogramData[v];
      resizeCanvas(state);
      drawHistogram(state);
    }
  }
}
